import { ArrowForwardIcon } from '@rsos/assets/icons';
import { ConditionalDisplay } from '@rsos/base-components';
import { FIELD_SHARING } from '@rsos/capstone/src/constants/capabilities';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import {
  NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG,
  useNorfolkSouthernAccessConditions,
} from '@rsos/hooks/useHasAccessToNorfolkSouthern';
import useHasCapability from '@rsos/hooks/useHasCapability';
import {
  useGetNorfolkSouthernRequestsQuery,
  useSelectedRequestId,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import ShareEvent from '../../../../ShareEvent/ShareEvent';
import { NORFOLK_SOUTHERN_RESOURCE } from '../../../../ShareEvent/constants/ShareEvent.types';
import styles from '../ResourceRequestPanel/QueueCategoryToggleButton.module.css';

function renderShareTrigger(onClick: () => void) {
  return (
    <button
      type="button"
      className={styles.toggleButton}
      onClick={onClick}
      title="Share"
      aria-label="Share"
      data-name="norfolk-southern-share"
    >
      <ArrowForwardIcon color="secondaryText" width={12} height={12} />
    </button>
  );
}

/**
 * Opens Field Share for the open consist request. Needs both the Norfolk
 * Southern gate and the Field Share gate, and a completed consist.
 */
export function NorfolkSouthernShareButton() {
  const { isFlagEnabled, hasCapability, hasPermission } =
    useNorfolkSouthernAccessConditions();
  const uniteSharingFlag = useBooleanFlagValue('unite_sharing', false);
  const fieldSharingCapability = useHasCapability(FIELD_SHARING);
  const selectedRequestId = useSelectedRequestId();
  const { data } = useGetNorfolkSouthernRequestsQuery();

  const request = data?.find(r => String(r.id) === selectedRequestId);

  if (!request) {
    return null;
  }

  return (
    <ConditionalDisplay
      dataName="norfolk-southern-share-button"
      conditions={[
        {
          showIf: isFlagEnabled,
          reason: `Needs the ${NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG} flag`,
        },
        {
          showIf: hasCapability,
          reason:
            'Agency needs the Norfolk Southern Consist Requesting capability',
        },
        {
          showIf: hasPermission,
          reason:
            'User role needs the NORFOLK_SOUTHERN_CONSIST_REQUESTING permission',
        },
        {
          showIf: Boolean(uniteSharingFlag),
          reason: 'Needs the unite_sharing feature flag',
        },
        {
          showIf: Boolean(fieldSharingCapability?.hasCapability),
          reason:
            'Agency is missing the field_sharing_0 capability (Andromeda > account > capabilities)',
        },
        {
          showIf: request.request.consistState === 'completed',
          reason: 'Consist request failed, so there is no consist to share',
        },
      ]}
    >
      <ShareEvent
        resource={NORFOLK_SOUTHERN_RESOURCE}
        renderTrigger={renderShareTrigger}
      />
    </ConditionalDisplay>
  );
}
